/*
🔔 Notifications Page
GetMyHive Operations Center - Bildirimler Sayfası
*/

import React, { useState } from 'react';
import {
  Card,
  List,
  Badge,
  Button,
  Space,
  Typography,
  Tag,
  Avatar,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Timeline,
  Tabs,
  Empty,
  Pagination,
  Tooltip,
  Modal,
  Form,
  Switch,
  Divider
} from 'antd';
import {
  BellOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  ClearOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  AlertOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// Mock notifications data - gerçek API'den gelecek
const mockNotifications = [
  {
    id: '1',
    type: 'security_alert',
    title: '🚨 Yüksek Risk Bölgesi Tespit Edildi',
    message: 'Taksim bölgesinde artan güvenlik riski tespit edildi. Acil güvenlik önlemleri devreye alındı.',
    timestamp: '2024-01-16 16:45',
    priority: 'high',
    isRead: false,
    source: 'Safety Intelligence',
    location: 'Taksim, İstanbul',
    actionRequired: true
  },
  {
    id: '2',
    type: 'harassment_increase',
    title: '📞 Telefon Taciz Artışı',
    message: 'Kadıköy bölgesinde telefon tacizi vakaları %35 artış gösterdi. Pattern analizi devam ediyor.',
    timestamp: '2024-01-16 15:30',
    priority: 'medium',
    isRead: false,
    source: 'AI Analytics',
    location: 'Kadıköy, İstanbul',
    actionRequired: false
  },
  {
    id: '3',
    type: 'ai_update',
    title: '🤖 AI Model Güncellendi',
    message: 'Tehdit tespit modeli v2.1 başarıyla deploy edildi. Tespit doğruluğu %12 arttı.',
    timestamp: '2024-01-16 14:15',
    priority: 'info',
    isRead: true,
    source: 'AI Training',
    location: null,
    actionRequired: false
  },
  {
    id: '4',
    type: 'emergency',
    title: '🆘 Aktif Acil Durum',
    message: 'Beşiktaş bölgesinde kayıp kişi arama operasyonu başlatıldı. ID: #EM2024-001',
    timestamp: '2024-01-16 13:20',
    priority: 'critical',
    isRead: true,
    source: 'Emergency Center',
    location: 'Beşiktaş, İstanbul',
    actionRequired: true
  },
  {
    id: '5',
    type: 'system',
    title: '🔧 Sistem Bakımı Tamamlandı',
    message: 'Veri toplama servisleri bakımı tamamlandı. Tüm sistemler normal çalışıyor.',
    timestamp: '2024-01-16 12:00',
    priority: 'low',
    isRead: true,
    source: 'System Admin',
    location: null,
    actionRequired: false
  },
  {
    id: '6',
    type: 'pattern_detection',
    title: '📊 Yeni Pattern Tespit Edildi',
    message: 'Şişli bölgesinde suç pattern analizi sonuçlandı. Risk seviyesi orta olarak belirlendi.',
    timestamp: '2024-01-16 11:45',
    priority: 'medium',
    isRead: true,
    source: 'Pattern Analysis',
    location: 'Şişli, İstanbul',
    actionRequired: false
  }
];

const NotificationsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState(mockNotifications);
  const [filteredNotifications, setFilteredNotifications] = useState(mockNotifications);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter and search logic
  React.useEffect(() => {
    let filtered = notifications;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(notification =>
        notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.source.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(notification => notification.priority === priorityFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(notification => 
        statusFilter === 'unread' ? !notification.isRead : notification.isRead
      );
    }

    setFilteredNotifications(filtered);
  }, [notifications, searchTerm, priorityFilter, statusFilter]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#ff7a45';
      case 'medium': return '#ffa940';
      case 'low': return '#52c41a';
      case 'info': return '#1890ff';
      default: return '#d9d9d9';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'critical': return 'KRİTİK';
      case 'high': return 'YÜKSEK';
      case 'medium': return 'ORTA';
      case 'low': return 'DÜŞÜK';
      case 'info': return 'BİLGİ';
      default: return 'BİLİNMEYEN';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'security_alert': return <WarningOutlined />;
      case 'harassment_increase': return <PhoneOutlined />;
      case 'ai_update': return <RobotOutlined />;
      case 'emergency': return <ExclamationCircleOutlined />;
      case 'system': return <SettingOutlined />;
      case 'pattern_detection': return <SafetyCertificateOutlined />;
      default: return <BellOutlined />;
    }
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, isRead: true }))
    );
  };

  const deleteNotification = (notificationId: string) => {
    setNotifications(prev => 
      prev.filter(notif => notif.id !== notificationId)
    );
  };

  const clearAllNotifications = () => {
    Modal.confirm({
      title: 'Tüm Bildirimleri Sil',
      content: 'Tüm bildirimleri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.',
      okText: 'Evet, Sil',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: () => {
        setNotifications([]);
      }
    });
  };

  const showNotificationDetail = (notification: any) => {
    setSelectedNotification(notification);
    setDetailModalVisible(true);
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  // Statistics
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const criticalCount = notifications.filter(n => n.priority === 'critical').length;
  const actionRequiredCount = notifications.filter(n => n.actionRequired && !n.isRead).length;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <BellOutlined style={{ color: '#1890ff' }} />
            🔔 Bildirimler
          </Space>
        </Title>
        <Text type="secondary">
          Sistem bildirimleri, güvenlik uyarıları ve operasyon merkezi mesajları
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Okunmamış"
              value={unreadCount}
              prefix={<BellOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Kritik"
              value={criticalCount}
              prefix={<AlertOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aksiyon Gerekli"
              value={actionRequiredCount}
              prefix={<ExclamationCircleOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam"
              value={notifications.length}
              prefix={<SafetyCertificateOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Search
              placeholder="Bildirim ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Öncelik"
              value={priorityFilter}
              onChange={setPriorityFilter}
            >
              <Option value="all">Tüm Öncelikler</Option>
              <Option value="critical">Kritik</Option>
              <Option value="high">Yüksek</Option>
              <Option value="medium">Orta</Option>
              <Option value="low">Düşük</Option>
              <Option value="info">Bilgi</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Durum"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">Tümü</Option>
              <Option value="unread">Okunmamış</Option>
              <Option value="read">Okunmuş</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Space wrap>
              <Button 
                type="primary" 
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
              >
                Tümünü Okundu İşaretle
              </Button>
              <Button 
                icon={<SettingOutlined />}
                onClick={() => setSettingsModalVisible(true)}
              >
                Ayarlar
              </Button>
              <Button 
                danger 
                icon={<ClearOutlined />}
                onClick={clearAllNotifications}
                disabled={notifications.length === 0}
              >
                Tümünü Sil
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Notifications List */}
      <Card>
        {filteredNotifications.length > 0 ? (
          <>
            <List
              dataSource={filteredNotifications.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
              renderItem={(notification) => (
                <List.Item
                  style={{
                    backgroundColor: notification.isRead ? 'transparent' : 'rgba(24, 144, 255, 0.02)',
                    border: notification.isRead ? 'none' : '1px solid rgba(24, 144, 255, 0.1)',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    padding: '16px'
                  }}
                  actions={[
                    <Tooltip title="Detayları Görüntüle">
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => showNotificationDetail(notification)}
                      />
                    </Tooltip>,
                    <Tooltip title="Sil">
                      <Button 
                        size="small" 
                        danger 
                        icon={<DeleteOutlined />}
                        onClick={() => deleteNotification(notification.id)}
                      />
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge dot={!notification.isRead}>
                        <Avatar 
                          icon={getTypeIcon(notification.type)}
                          style={{ backgroundColor: getPriorityColor(notification.priority) }}
                        />
                      </Badge>
                    }
                    title={
                      <Space>
                        <span style={{ 
                          fontWeight: notification.isRead ? 'normal' : 'bold',
                          color: notification.isRead ? 'inherit' : '#000'
                        }}>
                          {notification.title}
                        </span>
                        <Tag color={getPriorityColor(notification.priority)}>
                          {getPriorityText(notification.priority)}
                        </Tag>
                        {notification.actionRequired && (
                          <Tag color="orange">AKSIYON GEREKLİ</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <Text 
                          type="secondary"
                          style={{ 
                            fontWeight: notification.isRead ? 'normal' : '500'
                          }}
                        >
                          {notification.message}
                        </Text>
                        <Space size="large">
                          <Text type="secondary">
                            <ClockCircleOutlined /> {notification.timestamp}
                          </Text>
                          <Text type="secondary">
                            <SettingOutlined /> {notification.source}
                          </Text>
                          {notification.location && (
                            <Text type="secondary">
                              <EnvironmentOutlined /> {notification.location}
                            </Text>
                          )}
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            
            <Divider />
            
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredNotifications.length}
              onChange={setCurrentPage}
              onShowSizeChange={(current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} / ${total} bildirim`
              }
              style={{ textAlign: 'center', marginTop: 16 }}
            />
          </>
        ) : (
          <Empty 
            description="Bildirim bulunamadı"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Notification Detail Modal */}
      <Modal
        title="📋 Bildirim Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={600}
      >
        {selectedNotification && (
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Space>
                  <Avatar 
                    icon={getTypeIcon(selectedNotification.type)}
                    style={{ backgroundColor: getPriorityColor(selectedNotification.priority) }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedNotification.title}
                    </Title>
                    <Space>
                      <Tag color={getPriorityColor(selectedNotification.priority)}>
                        {getPriorityText(selectedNotification.priority)}
                      </Tag>
                      {selectedNotification.actionRequired && (
                        <Tag color="orange">AKSIYON GEREKLİ</Tag>
                      )}
                    </Space>
                  </div>
                </Space>
              </div>

              <div>
                <Text>{selectedNotification.message}</Text>
              </div>

              <div>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Text type="secondary">Zaman:</Text>
                    <br />
                    <Text strong>{selectedNotification.timestamp}</Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Kaynak:</Text>
                    <br />
                    <Text strong>{selectedNotification.source}</Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">Lokasyon:</Text>
                    <br />
                    <Text strong>{selectedNotification.location || 'Belirtilmemiş'}</Text>
                  </Col>
                </Row>
              </div>

              {selectedNotification.actionRequired && (
                <Card size="small" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
                  <Space>
                    <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                    <Text strong>Bu bildirim için aksiyon gereklidir.</Text>
                  </Space>
                </Card>
              )}
            </Space>
          </div>
        )}
      </Modal>

      {/* Settings Modal */}
      <Modal
        title="🔧 Bildirim Ayarları"
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSettingsModalVisible(false)}>
            Kapat
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="E-posta Bildirimleri">
            <Switch defaultChecked /> Kritik bildirimleri e-posta ile gönder
          </Form.Item>
          <Form.Item label="Sesli Bildirimler">
            <Switch defaultChecked /> Tarayıcı bildirimleri için ses çal
          </Form.Item>
          <Form.Item label="Masaüstü Bildirimleri">
            <Switch defaultChecked /> Tarayıcı masaüstü bildirimleri göster
          </Form.Item>
          <Form.Item label="Otomatik Temizleme">
            <Switch /> 30 günden eski bildirimleri otomatik sil
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationsPage;
